import React, { useMemo } from "react";
import { GenericTable, type GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { DataStateView } from "../../../kontrolEdge/components/Backbone/BackboneDetails/StateViews/DataStateView";
import { buildResourceRows, filterResourceRows, type InsightResourceRow } from "./insightResourceRows";
import type { InsightRequestState, InsightTopologyResponse } from "../../types/insightApi";

export interface InsightResourceListProps {
	topology: InsightTopologyResponse | null;
	status: InsightRequestState;
	errorMessage?: string | null;
	onRetry: () => void;
	selectedResourceId: string | null;
	onSelectResource: (id: string) => void;
	/** The same lifted search string the canvas uses, so a query applies to whichever representation is showing. */
	search: string;
	/** Truthful blocking copy for the zero-usable-topology case, mirrored from the canvas so both representations explain an empty result the same way. */
	emptyDescription?: string;
}

/** Small category-tinted target badge (IGW/NAT/TGW…) — the thing that actually distinguishes one route table from another, so it reads as the hero of the row rather than a buried pill. */
function TargetBadges({ targets }: { targets: InsightResourceRow["targets"] }) {
	if (targets.length === 0) return <span className="text-semantic-text-tertiary">—</span>;
	return (
		<div className="flex flex-wrap gap-1">
			{targets.map((target, index) => (
				<span
					key={`${target.kind}-${target.label}-${index}`}
					title={target.label}
					className="truncate rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:border-semantic-border-success-primary dark:bg-semantic-bg-success-secondary dark:text-semantic-text-success"
				>
					{target.label}
				</span>
			))}
		</div>
	);
}

const STATUS_VARIANT: Record<"warning" | "error", "warning" | "error"> = { warning: "warning", error: "error" };

function StatusCell({ status }: { status: InsightResourceRow["status"] }) {
	if (status === "warning" || status === "error") {
		return (
			<GenericTag
				label={status === "error" ? "Error" : "Warning"}
				variant={STATUS_VARIANT[status]}
				size="small"
			/>
		);
	}
	return <span className="text-semantic-text-tertiary">—</span>;
}

/** Severity rank so the Status column sorts error > warning > ok/none rather than alphabetically. */
function statusRank(status: InsightResourceRow["status"]): number {
	if (status === "error") return 2;
	if (status === "warning") return 1;
	return 0;
}

/**
 * Dense, sortable, filterable table representation of a topology's resources
 * (CTX-* "List" view). The readable default for a flat backbone source whose
 * dozens of root-parented route tables/gateways collapse into an illegible
 * card wall on the canvas — here they're one scannable table, sorted by type
 * then name, with route count / connectivity targets / associated-subnet
 * counts as first-class sortable columns. Selecting a row drives the same
 * `ResourceDetailPanel` the canvas uses, via the shared `onSelectResource`.
 */
export const InsightResourceList: React.FC<InsightResourceListProps> = ({
	topology,
	status,
	errorMessage,
	onRetry,
	selectedResourceId,
	onSelectResource,
	search,
	emptyDescription,
}) => {
	const rows = useMemo(() => filterResourceRows(buildResourceRows(topology), search), [topology, search]);

	const columns = useMemo<GenericTableColumn<InsightResourceRow>[]>(
		() => [
			{
				id: "type",
				header: "Type",
				width: "150px",
				fixed: "left",
				sortable: true,
				sortAccessor: (row) => row.typeLabel,
				renderCell: (row) => (
					<span className="text-xs font-semibold text-semantic-text-primary">{row.typeLabel}</span>
				),
			},
			{
				id: "name",
				header: "Name",
				width: "220px",
				sortable: true,
				wrapMode: "truncate",
				sortAccessor: (row) => row.name.toLowerCase(),
				renderCell: (row) => (
					<span className="truncate font-medium text-semantic-text-primary" title={row.name}>
						{row.name}
					</span>
				),
			},
			{
				id: "identifier",
				header: "Identifier",
				width: "220px",
				sortable: true,
				wrapMode: "truncate",
				sortAccessor: (row) => row.nativeId.toLowerCase(),
				renderCell: (row) => (
					<span className="truncate font-mono text-[11px] text-semantic-text-secondary" title={row.nativeId}>
						{row.nativeId}
					</span>
				),
			},
			{
				id: "scope",
				header: "Scope",
				width: "160px",
				sortable: true,
				wrapMode: "truncate",
				sortAccessor: (row) => row.scopeLabel.toLowerCase(),
				renderCell: (row) =>
					row.scopeLabel ? (
						<span className="truncate text-xs text-semantic-text-secondary" title={row.scopeLabel}>
							{row.scopeLabel}
						</span>
					) : (
						<span className="text-semantic-text-tertiary">—</span>
					),
			},
			{
				id: "routes",
				header: "Routes",
				width: "90px",
				sortable: true,
				sortAccessor: (row) => row.routeCount ?? -1,
				renderCell: (row) =>
					row.routeCount == null ? (
						<span className="text-semantic-text-tertiary">—</span>
					) : (
						<span className="tabular-nums text-xs font-medium text-semantic-text-primary">{row.routeCount}</span>
					),
			},
			{
				id: "targets",
				header: "Targets",
				width: "200px",
				sortable: true,
				sortAccessor: (row) => row.targets.length,
				renderCell: (row) => <TargetBadges targets={row.targets} />,
			},
			{
				id: "subnets",
				header: "Subnets",
				width: "100px",
				sortable: true,
				sortAccessor: (row) => row.associatedSubnets ?? -1,
				renderCell: (row) =>
					row.associatedSubnets == null ? (
						<span className="text-semantic-text-tertiary">—</span>
					) : (
						<span className="tabular-nums text-xs text-semantic-text-secondary">{row.associatedSubnets}</span>
					),
			},
			{
				id: "status",
				header: "Status",
				width: "110px",
				sortable: true,
				sortAccessor: (row) => statusRank(row.status),
				renderCell: (row) => <StatusCell status={row.status} />,
			},
		],
		[]
	);

	const selectedRowKeys = useMemo(
		() => (selectedResourceId ? new Set<string>([selectedResourceId]) : undefined),
		[selectedResourceId]
	);

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary">
			<DataStateView
				loading={status === "loading" && !topology}
				loadingContent={<TableSkeleton />}
				error={status === "error" ? errorMessage || "Failed to load topology" : null}
				errorTitle="Failed to load topology"
				onRetry={onRetry}
				isEmpty={rows.length === 0}
				emptyTitle={search ? "No matching resources" : "No resources to list"}
				emptyDescription={
					search
						? "Try a different search term, or clear the search to see every resource."
						: emptyDescription || "This source produced no route tables, gateways, or other listable resources."
				}
			>
				<div className="min-h-0 flex-1 overflow-hidden" data-testid="insight-resource-list">
					{/* maxTableBodyHeight="100%" switches GenericTable to its flex
					    layout — without it the body caps at a fixed 400px and strands
					    the rest of the viewport ("look how much vertical space we are
					    missing"). */}
					<GenericTable
						columns={columns}
						data={rows}
						rowKey={(row) => row.id}
						onRowClick={(row) => onSelectResource(row.id)}
						selectedRowKeys={selectedRowKeys}
						initialRowsPerPage={25}
						rowsPerPageOptions={[25, 50, 100]}
						horizontalScroll="lg-fit"
						width="100%"
						maxTableBodyHeight="100%"
					/>
				</div>
			</DataStateView>
		</div>
	);
};

function TableSkeleton() {
	return (
		<div className="flex flex-col gap-2 p-3" data-testid="resource-list-loading">
			{Array.from({ length: 6 }).map((_, index) => (
				<div key={index} className="h-9 w-full animate-pulse rounded-sm bg-semantic-bg-tertiary" />
			))}
		</div>
	);
}

export default InsightResourceList;
